import { Controller } from "@hotwired/stimulus"
import { loadStripe, cardBrandLabel } from "../lib/stripe_loader"
// braintree-web is a megabyte of script that only checkout pages need — loaded on
// demand so it stays out of the entry bundle every other page boots with.

/**
 * Braintree Hosted Fields Controller
 * 
 * Handles the Braintree Hosted Fields integration for secure card payments
 * and US Bank Account (ACH) payments.
 *
 * Since TMS-1789 the card can go through Stripe instead: with card-provider "stripe" the card
 * fields are Stripe's Payment Element (deferred mode, shown the amount) and the token posted as
 * payment_method_nonce is a Stripe ConfirmationToken. ACH stays on Braintree either way, so
 * Braintree still loads on a page that offers it.
 * 
 * Usage:
 *   <div data-controller="braintree-hosted-fields"
 *        data-braintree-hosted-fields-client-token-url-value="/payments/braintree/client_token"
 *        data-braintree-hosted-fields-card-payment-url-value="/payments/braintree/card_payment"
 *        data-braintree-hosted-fields-ach-payment-url-value="/payments/braintree/ach_payment">
 *     ...
 *   </div>
 */
export default class extends Controller {
  static targets = [
    "form",
    "cardOption",
    "cardFields",
    "cardFieldsContent",
    "cardFieldsLoader",
    "stripeElement",
    "cardMethodOption",
    "cardNumber",
    "cardholderName",
    "expirationDate",
    "cvv",
    "achOption",
    "achFields",
    "achMethodOption",
    "achAccountHolderName",
    "achRoutingNumber",
    "achAccountNumber",
    "achAccountTypeChecking",
    "achAccountTypeSavings",
    "achAuthorization",
    "achAccountHolderNameError",
    "achRoutingNumberError",
    "achAccountNumberError",
    "stateContainer",
    "billingStateSelect",
    "billingStateInput",
    "nonce",
    "cardBrand",
    "cardLastFour",
    "submitButton",
    "errorMessage",
    "checkoutPaymentError",
    "checkoutPaymentErrorMessage",
    "cardNumberError",
    "cardholderNameError",
    "expirationDateError",
    "cvvError"
  ]

  static values = {
    clientTokenUrl: String,
    cardPaymentUrl: String,
    achPaymentUrl: String,
    paymentId: String,
    cardOnly: { type: String, default: "false" },
    cardProvider: { type: String, default: "braintree" },
    stripePublishableKey: String,
    stripeAmount: Number,
    stripeCurrency: { type: String, default: "usd" }
  }

  connect() {
    this.hostedFieldsInstance = null
    this.usBankAccountInstance = null
    this.clientInstance = null
    this.isSubmitting = false
    this.cardFieldsReady = false
    this.selectedPaymentMethod = null
    if (this.stripeCards) this.initializeStripe()
    if (!this.stripeCards || this.cardOnlyValue !== "true") this.initializeBraintree()
    this.syncStateFieldVisibility()
    this.listenForCountryChange()

    if (this.cardOnlyValue === "true") {
      this.selectCard()
    }
  }

  get stripeCards() {
    return this.cardProviderValue === "stripe"
  }

  disconnect() {
    if (this.paymentElement) {
      this.paymentElement.destroy()
    }
    if (this.hostedFieldsInstance) {
      this.hostedFieldsInstance.teardown()
    }
    if (this.usBankAccountInstance) {
      this.usBankAccountInstance.teardown()
    }
    if (this.boundCountryChanged) {
      const countryHiddenInput = this.element.querySelector('[name="payment[payment_info_attributes][country_code]"]')
      if (countryHiddenInput) {
        countryHiddenInput.removeEventListener('app-select:change', this.boundCountryChanged)
      }
    }
  }

  async initializeBraintree() {
    try {
      const response = await fetch(this.clientTokenUrlValue, {
        headers: {
          "Accept": "application/json",
          "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content
        }
      })

      if (!response.ok) {
        throw new Error("Failed to fetch client token")
      }

      const data = await response.json()

      this.braintree = (await import("braintree-web")).default
      this.clientInstance = await this.braintree.client.create({
        authorization: data.client_token
      })

      const tasks = this.stripeCards ? [] : [this.createHostedFields(this.clientInstance)]
      if (this.cardOnlyValue !== "true") {
        tasks.push(this.createUsBankAccount(this.clientInstance))
      }
      await Promise.all(tasks)
    } catch (error) {
      console.error("Braintree initialization error:", error)
      this.showError("Failed to initialize payment form. Please refresh and try again.")
    }
  }

  async initializeStripe() {
    try {
      this.stripe = await loadStripe(this.stripePublishableKeyValue)
      this.stripeElements = this.stripe.elements({
        mode: "payment",
        amount: this.stripeAmountValue,
        currency: this.stripeCurrencyValue,
        paymentMethodTypes: ["card"],
        appearance: {
          variables: { colorText: "#1A1C43", colorDanger: "#EF4444" }
        }
      })
      this.paymentElement = this.stripeElements.create("payment")
      this.paymentElement.on("ready", () => {
        this.cardFieldsReady = true
        this.hideLoader()
      })
      this.paymentElement.mount(this.stripeElementTarget)
    } catch (error) {
      console.error("Stripe initialization error:", error)
      this.showError("Failed to initialize payment form. Please refresh and try again.")
      this.hideLoader()
    }
  }

  async createUsBankAccount(clientInstance) {
    try {
      this.usBankAccountInstance = await this.braintree.usBankAccount.create({
        client: clientInstance
      })
    } catch (error) {
      console.error("US Bank Account creation error:", error)
    }
  }

  async createHostedFields(clientInstance) {
    const styles = {
      "input": {
        "font-size": "16px",
        "font-family": "inherit",
        "font-weight": "600",
        "color": "#1A1C43"
      },
      "input::placeholder": {
        "color": "#737373",
        "font-weight": "400"
      },
      "input.invalid": {
        "color": "#EF4444"
      },
      "input.valid": {
        "color": "#1A1C43"
      }
    }

    try {
      this.hostedFieldsInstance = await this.braintree.hostedFields.create({
        client: clientInstance,
        styles: styles,
        fields: {
          number: {
            container: this.cardNumberTarget,
            placeholder: "1234 1234 1234 1234"
          },
          cardholderName: {
            container: this.cardholderNameTarget,
            placeholder: "Full Name"
          },
          expirationDate: {
            container: this.expirationDateTarget,
            placeholder: "MM/YY"
          },
          cvv: {
            container: this.cvvTarget,
            placeholder: "CVV"
          }
        }
      })

      // Listen for validation events
      this.hostedFieldsInstance.on("validityChange", (event) => {
        this.handleValidityChange(event)
      })

      this.hostedFieldsInstance.on("focus", (event) => {
        this.handleFocus(event)
      })

      this.hostedFieldsInstance.on("blur", (event) => {
        this.handleBlur(event)
      })

      // Mark as ready and update UI
      this.cardFieldsReady = true
      this.hideLoader()

    } catch (error) {
      console.error("Hosted Fields creation error:", error)
      this.showError("Failed to load payment fields. Please refresh and try again.")
      this.hideLoader()
    }
  }

  showLoader() {
    if (this.hasCardFieldsLoaderTarget) {
      this.cardFieldsLoaderTarget.classList.remove("d-none")
    }
    if (this.hasCardFieldsContentTarget) {
      this.cardFieldsContentTarget.classList.add("d-none")
    }
  }

  hideLoader() {
    if (this.hasCardFieldsLoaderTarget) {
      this.cardFieldsLoaderTarget.classList.add("d-none")
    }
    if (this.hasCardFieldsContentTarget) {
      this.cardFieldsContentTarget.classList.remove("d-none")
    }
  }

  handleValidityChange(event) {
    const field = event.fields[event.emittedBy]
    const errorTarget = this.getErrorTarget(event.emittedBy)
    
    if (field.isValid) {
      this.clearFieldError(event.emittedBy, errorTarget)
    } else if (field.isPotentiallyValid) {
      this.clearFieldError(event.emittedBy, errorTarget)
    } else {
      this.showFieldError(event.emittedBy, errorTarget)
    }
  }

  handleFocus(event) {
    const container = this.getFieldContainer(event.emittedBy)
    if (container) {
      container.classList.add("focused")
    }
  }

  handleBlur(event) {
    const container = this.getFieldContainer(event.emittedBy)
    if (container) {
      container.classList.remove("focused")
    }
  }

  getFieldContainer(fieldName) {
    switch (fieldName) {
      case "number": return this.cardNumberTarget.closest(".hosted-field-container")
      case "cardholderName": return this.cardholderNameTarget.closest(".hosted-field-container")
      case "expirationDate": return this.expirationDateTarget.closest(".hosted-field-container")
      case "cvv": return this.cvvTarget.closest(".hosted-field-container")
      default: return null
    }
  }

  getErrorTarget(fieldName) {
    switch (fieldName) {
      case "number": return this.hasCardNumberErrorTarget ? this.cardNumberErrorTarget : null
      case "cardholderName": return this.hasCardholderNameErrorTarget ? this.cardholderNameErrorTarget : null
      case "expirationDate": return this.hasExpirationDateErrorTarget ? this.expirationDateErrorTarget : null
      case "cvv": return this.hasCvvErrorTarget ? this.cvvErrorTarget : null
      default: return null
    }
  }

  showFieldError(fieldName, errorTarget) {
    const container = this.getFieldContainer(fieldName)
    if (container) {
      container.classList.add("has-error")
    }
    if (errorTarget) {
      const messages = {
        number: "Please enter a valid card number",
        cardholderName: "Please enter the cardholder name",
        expirationDate: "Please enter a valid expiration date",
        cvv: "Please enter a valid CVV"
      }
      errorTarget.textContent = messages[fieldName] || "Invalid field"
      errorTarget.classList.remove("d-none")
    }
  }

  clearFieldError(fieldName, errorTarget) {
    const container = this.getFieldContainer(fieldName)
    if (container) {
      container.classList.remove("has-error")
    }
    if (errorTarget) {
      errorTarget.textContent = ""
      errorTarget.classList.add("d-none")
    }
  }

  selectCard(event) {
    this.selectedPaymentMethod = 'card'
    
    // Update radio buttons
    if (this.hasCardOptionTarget) this.cardOptionTarget.checked = true
    if (this.hasAchOptionTarget) this.achOptionTarget.checked = false
    
    // Update option card styles
    if (this.hasCardMethodOptionTarget) this.cardMethodOptionTarget.classList.add("selected")
    if (this.hasAchMethodOptionTarget) this.achMethodOptionTarget.classList.remove("selected")
    
    // Expand/collapse fields
    if (this.hasCardFieldsTarget) this.cardFieldsTarget.classList.add("expanded")
    if (this.hasAchFieldsTarget) this.achFieldsTarget.classList.remove("expanded")

    // Update form action URL
    if (this.hasCardPaymentUrlValue) {
      this.formTarget.action = this.cardPaymentUrlValue
    }

    // Show loader if the card fields are not ready yet
    if (!this.cardFieldsReady) {
      this.showLoader()
    }
  }

  selectAch(event) {
    this.selectedPaymentMethod = 'ach'
    
    // Update radio buttons
    if (this.hasAchOptionTarget) this.achOptionTarget.checked = true
    if (this.hasCardOptionTarget) this.cardOptionTarget.checked = false
    
    // Update option card styles
    if (this.hasAchMethodOptionTarget) this.achMethodOptionTarget.classList.add("selected")
    if (this.hasCardMethodOptionTarget) this.cardMethodOptionTarget.classList.remove("selected")
    
    // Expand/collapse fields
    if (this.hasAchFieldsTarget) this.achFieldsTarget.classList.add("expanded")
    if (this.hasCardFieldsTarget) this.cardFieldsTarget.classList.remove("expanded")

    // Update form action URL
    if (this.hasAchPaymentUrlValue) {
      this.formTarget.action = this.achPaymentUrlValue
    }
  }

  async submit(event) {
    event.preventDefault()

    if (this.isSubmitting) return

    // Check if a payment method is selected
    const isCardSelected = this.hasCardOptionTarget && this.cardOptionTarget.checked
    const isAchSelected = this.hasAchOptionTarget && this.achOptionTarget.checked

    if (!isCardSelected && !isAchSelected) {
      this.showError("Please select a payment method")
      return
    }

    this.isSubmitting = true
    this.setLoading(true)
    this.clearError()

    try {
      let nonce

      if (isCardSelected) {
        nonce = await this.tokenizeCard()
      } else if (isAchSelected) {
        nonce = await this.tokenizeAch()
      }

      // Set the nonce in the hidden field
      this.nonceTarget.value = nonce

      // Submit the form
      this.formTarget.submit()

    } catch (error) {
      this.handleTokenizationError(error)
      this.isSubmitting = false
      this.setLoading(false)
    }
  }

  async tokenizeCard() {
    if (this.stripeCards) return this.tokenizeStripeCard()

    if (!this.hostedFieldsInstance) {
      throw new Error("Payment form not ready. Please wait and try again.")
    }

    const { nonce, details } = await this.hostedFieldsInstance.tokenize()
    // Pages that show the card back to the buyer before charging it (the Store's Review step)
    // keep its brand and last four; nothing else about the card leaves Braintree's fields.
    if (this.hasCardBrandTarget) this.cardBrandTarget.value = details?.cardType || ""
    if (this.hasCardLastFourTarget) this.cardLastFourTarget.value = details?.lastFour || ""
    return nonce
  }

  // Nothing is charged here: the ConfirmationToken is what the server confirms a PaymentIntent
  // with once the buyer pays, as a Braintree nonce is for a sale.
  async tokenizeStripeCard() {
    if (!this.stripeElements) {
      throw new Error("Payment form not ready. Please wait and try again.")
    }

    const { error: submitError } = await this.stripeElements.submit()
    if (submitError) throw { message: submitError.message, isValidationError: true }

    const { error, confirmationToken } = await this.stripe.createConfirmationToken({ elements: this.stripeElements })
    if (error) throw { message: error.message, isValidationError: true }

    const card = confirmationToken.payment_method_preview?.card
    if (this.hasCardBrandTarget) this.cardBrandTarget.value = cardBrandLabel(card?.brand)
    if (this.hasCardLastFourTarget) this.cardLastFourTarget.value = card?.last4 || ""
    return confirmationToken.id
  }

  async tokenizeAch() {
    if (!this.usBankAccountInstance) {
      throw new Error("ACH payment form not ready. Please wait and try again.")
    }

    // Validate ACH fields
    const validationError = this.validateAchFields()
    if (validationError) {
      throw { message: validationError, isValidationError: true }
    }

    // Get billing info to determine account holder type
    const companyInput = document.querySelector('[name="payment[payment_info_attributes][company]"]')
    const firstNameInput = document.querySelector('[name="payment[payment_info_attributes][first_name]"]')
    const lastNameInput = document.querySelector('[name="payment[payment_info_attributes][last_name]"]')
    const streetAddressInput = document.querySelector('[name="payment[payment_info_attributes][address_line_one]"]')
    const extendedAddressInput = document.querySelector('[name="payment[payment_info_attributes][address_line_two]"]')
    const postalCodeInput = document.querySelector('[name="payment[payment_info_attributes][postal_code]"]')
    const cityInput = document.querySelector('[name="payment[payment_info_attributes][city]"]')
    const stateInput = document.querySelector('[name="payment[payment_info_attributes][state]"]')

    const isBusinessAccount = companyInput && companyInput.value.trim() !== ''
    const ownershipType = isBusinessAccount ? 'business' : 'personal'

    const bankDetails = {
      accountNumber: this.achAccountNumberTarget.value.trim(),
      routingNumber: this.achRoutingNumberTarget.value.trim(),
      accountType: this.achAccountTypeCheckingTarget.checked ? 'checking' : 'savings',
      ownershipType: ownershipType,
      billingAddress: {
        streetAddress: streetAddressInput?.value || '',
        extendedAddress: extendedAddressInput?.value || '',
        locality: cityInput?.value?.trim() || '',
        region: stateInput?.value?.trim() || '',
        postalCode: postalCodeInput?.value || ''
      }
    }

    // Add ownership-specific fields
    if (isBusinessAccount) {
      bankDetails.businessName = companyInput.value.trim()
    } else {
      bankDetails.firstName = firstNameInput?.value || ''
      bankDetails.lastName = lastNameInput?.value || ''
    }

    const { nonce } = await this.usBankAccountInstance.tokenize({
      bankDetails: bankDetails,
      mandateText: this.achAuthorizationTarget.innerText.trim()
    })

    return nonce
  }

  validateAchFields() {
    let hasError = false
    this.clearAchFieldErrors()

    // Validate account holder name
    if (!this.hasAchAccountHolderNameTarget || !this.achAccountHolderNameTarget.value.trim()) {
      this.showAchFieldError('achAccountHolderName', 'Please enter the account holder name')
      hasError = true
    }

    // Validate routing number (must be 9 digits)
    const routingNumber = this.hasAchRoutingNumberTarget ? this.achRoutingNumberTarget.value.trim() : ''
    if (!routingNumber || !/^\d{9}$/.test(routingNumber)) {
      this.showAchFieldError('achRoutingNumber', 'Please enter a valid 9-digit routing number')
      hasError = true
    }

    // Validate account number (4-17 digits)
    const accountNumber = this.hasAchAccountNumberTarget ? this.achAccountNumberTarget.value.trim() : ''
    if (!accountNumber || !/^\d{4,17}$/.test(accountNumber)) {
      this.showAchFieldError('achAccountNumber', 'Please enter a valid account number')
      hasError = true
    }

    // Validate city
    const cityInput = document.querySelector('[name="payment[payment_info_attributes][city]"]')
    if (!cityInput || !cityInput.value.trim()) {
      hasError = true
    }

    // Validate state
    const stateInput = document.querySelector('[name="payment[payment_info_attributes][state]"]')
    if (!stateInput || !stateInput.value.trim()) {
      hasError = true
    }

    if (hasError) {
      return 'Please correct the errors in the ACH form'
    }

    return null
  }

  showAchFieldError(fieldName, message) {
    const errorTarget = this[`${fieldName}ErrorTarget`]
    if (errorTarget) {
      errorTarget.textContent = message
      errorTarget.classList.remove('d-none')
    }

    // Add error styling to input container
    const inputTarget = this[`${fieldName}Target`]
    if (inputTarget) {
      const container = inputTarget.closest('.app-input-container')
      if (container) {
        container.classList.add('has-error')
      }
    }
  }

  clearAchFieldErrors() {
    const achErrorTargets = [
      'achAccountHolderNameError',
      'achRoutingNumberError', 
      'achAccountNumberError'
    ]

    achErrorTargets.forEach(targetName => {
      if (this[`has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`]) {
        const errorTarget = this[`${targetName}Target`]
        if (errorTarget) {
          errorTarget.textContent = ''
          errorTarget.classList.add('d-none')
        }
      }
    })

    // Remove error styling from ACH input containers
    const achInputTargets = [
      'achAccountHolderName',
      'achRoutingNumber',
      'achAccountNumber'
    ]

    achInputTargets.forEach(targetName => {
      const hasTarget = `has${targetName.charAt(0).toUpperCase() + targetName.slice(1)}Target`
      if (this[hasTarget]) {
        const inputTarget = this[`${targetName}Target`]
        if (inputTarget) {
          const container = inputTarget.closest('.app-input-container')
          if (container) {
            container.classList.remove('has-error')
          }
        }
      }
    })
  }

  handleTokenizationError(error) {
    let message = "An error occurred while processing your payment. Please try again."

    if (error.isValidationError) {
      message = error.message
    } else if (error.code) {
      switch (error.code) {
        // Card errors
        case "HOSTED_FIELDS_FIELDS_EMPTY":
          message = "Please fill in all card details"
          break
        case "HOSTED_FIELDS_FIELDS_INVALID":
          message = "Please check your card details and try again"
          break
        case "HOSTED_FIELDS_TOKENIZATION_FAIL_ON_DUPLICATE":
          message = "This card has already been used. Please use a different card."
          break
        case "HOSTED_FIELDS_TOKENIZATION_CVV_VERIFICATION_FAILED":
          message = "CVV verification failed. Please check your CVV."
          break
        case "HOSTED_FIELDS_TOKENIZATION_NETWORK_ERROR":
          message = "Network error. Please check your connection and try again."
          break
        // ACH/US Bank Account errors
        case "US_BANK_ACCOUNT_OPTION_REQUIRED":
          message = "Bank account information is required."
          break
        case "US_BANK_ACCOUNT_MUTUALLY_EXCLUSIVE_OPTIONS":
          message = "Invalid bank account configuration."
          break
        case "US_BANK_ACCOUNT_BANK_LOGIN_NOT_ENABLED":
        case "US_BANK_ACCOUNT_LOGIN_LOAD_FAILED":
          message = "Bank login is not available. Please enter your bank details manually."
          break
        case "US_BANK_ACCOUNT_LOGIN_CLOSED":
          message = "Bank login was closed. Please try again."
          break
        case "US_BANK_ACCOUNT_LOGIN_REQUEST_ACTIVE":
          message = "A bank login request is already in progress."
          break
        case "US_BANK_ACCOUNT_TOKENIZATION_NETWORK_ERROR":
          message = "Network error while processing bank account. Please try again."
          break
      }
    } else if (error.message) {
      message = error.message
    }

    this.showError(message)
  }

  showError(message) {
    if (this.hasCheckoutPaymentErrorTarget) {
      this.showCheckoutPaymentError(message)
      return
    }

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = message
      this.errorMessageTarget.classList.remove("d-none")
    }
  }

  showCheckoutPaymentError(message) {
    if (this.hasCheckoutPaymentErrorMessageTarget) {
      this.checkoutPaymentErrorMessageTarget.textContent = message
    }

    this.checkoutPaymentErrorTarget.classList.remove("d-none")
    this.checkoutPaymentErrorTarget.scrollIntoView({ behavior: "smooth", block: "nearest" })
  }

  clearError() {
    if (this.hasCheckoutPaymentErrorTarget) {
      this.checkoutPaymentErrorTarget.classList.add("d-none")
    }

    if (this.hasErrorMessageTarget) {
      this.errorMessageTarget.textContent = ""
      this.errorMessageTarget.classList.add("d-none")
    }
  }

  setLoading(loading) {
    if (this.hasSubmitButtonTarget) {
      this.submitButtonTarget.disabled = loading
      if (loading) {
        this.submitButtonTarget.dataset.originalText = this.submitButtonTarget.textContent
        this.submitButtonTarget.textContent = "Processing..."
      } else {
        this.submitButtonTarget.textContent = this.submitButtonTarget.dataset.originalText || "Complete Purchase"
      }
    }
  }

  countryChanged() {
    this.syncStateFieldVisibility()
  }

  listenForCountryChange() {
    const countryHiddenInput = this.element.querySelector('[name="payment[payment_info_attributes][country_code]"]')
    if (countryHiddenInput) {
      this.boundCountryChanged = this.countryChanged.bind(this)
      countryHiddenInput.addEventListener('app-select:change', this.boundCountryChanged)
    }
  }

  syncStateFieldVisibility() {
    if (!this.hasStateContainerTarget) return

    const countrySelect = document.querySelector('[name="payment[payment_info_attributes][country_code]"]')
    // The custom select component stores its value in a hidden input with class 'select-hidden-input-value'
    const countryContainer = countrySelect?.closest('.app-select-container')
    const countryHiddenInput = countryContainer?.querySelector('.select-hidden-input-value')
    const selectedCountry = countryHiddenInput?.value || countrySelect?.value || ''
    const isUS = selectedCountry === 'US'

    if (this.hasBillingStateSelectTarget) {
      const selectWrapper = this.billingStateSelectTarget
      const selectHiddenInput = selectWrapper.querySelector('.select-hidden-input-value')

      if (isUS) {
        selectWrapper.classList.remove('d-none')
        if (selectHiddenInput) selectHiddenInput.name = 'payment[payment_info_attributes][state]'
      } else {
        selectWrapper.classList.add('d-none')
        if (selectHiddenInput) selectHiddenInput.name = ''
      }
    }

    if (this.hasBillingStateInputTarget) {
      const inputWrapper = this.billingStateInputTarget
      const textInput = inputWrapper.querySelector('input[type="text"]')

      if (isUS) {
        inputWrapper.classList.add('d-none')
        if (textInput) textInput.name = ''
      } else {
        inputWrapper.classList.remove('d-none')
        if (textInput) textInput.name = 'payment[payment_info_attributes][state]'
      }
    }
  }
}
